namespace $ {

	export class $shm_hitalama_map_history_undo {

		private static instances = new WeakMap< object, Map< string, $shm_hitalama_map_history_undo > >

		private constructor( readonly history: $shm_hitalama_map_history ) {}

		static for( history: $shm_hitalama_map_history ) {
			const land = history.land()
			let land_instances = this.instances.get( land )
			if( !land_instances ) this.instances.set( land, land_instances = new Map )
			const id = history.link().str
			let instance = land_instances.get( id )
			if( !instance ) land_instances.set( id, instance = new this( history ) )
			return instance
		}

		/** Read older summaries only when the next own action is not in this page. */
		private * entries() {
			const size = $shm_hitalama_map_history.storage_recent_size
			const peer = this.history.$.$giper_baza_auth.current().pass().peer().str
			for( let offset = 0; ; offset += size ) {
				const page = this.history.entries_page_retained_peer(peer,size,offset)
				yield* page.events
				if( !page.events.length || offset + page.events.length >= page.count ) return
			}
		}

		undo_candidate() {
			const peer = this.history.$.$giper_baza_auth.current().pass().peer().str
			const controls = new Map< string, boolean >
			for( const event of this.entries() ) {
				const data = event.data()
				if( event.peer() !== peer || !data ) continue
				if( data.origin && !controls.has( data.origin ) ) {
					if( data.source === 'undo' || data.source === 'redo' ) controls.set( data.origin, data.source === 'undo' )
				}
				if( data.source === 'user' && !controls.get( event.link().str ) ) return event.full()
			}
			return null
		}

		redo_candidate() {
			const peer = this.history.$.$giper_baza_auth.current().pass().peer().str
			const repeated = new Map< string, number >
			for( const event of this.entries() ) {
				if( event.peer() !== peer ) continue
				const data = event.data()
				if( !data ) continue
				if( data.source === 'user' ) return null
				if( data.source === 'redo' && data.origin ) {
					repeated.set( data.origin, ( repeated.get( data.origin ) ?? 0 ) + 1 )
				}
				if( data.source === 'undo' && data.origin ) {
					const count = repeated.get( data.origin ) ?? 0
					if( count ) { repeated.set( data.origin, count - 1 ); continue }
					const original = this.history.event_by_link( data.origin )
					return [ data.origin, original ] as const
				}
			}
			return null
		}

		@ $mol_action
		undo() {
			const event = $mol_wire_sync( this ).undo_candidate()
			if( !event ) return false
			const data = $mol_wire_sync( event ).data_ready()
			$mol_wire_sync( this ).apply( data.changes, false )
			this.control_add( 'undo', event, data.title )
			return true
		}

		@ $mol_action
		redo() {
			const candidate = $mol_wire_sync( this ).redo_candidate()
			if( !candidate ) return false
			const [ origin, event ] = candidate
			const data = $mol_wire_sync( event ).data_ready()
			$mol_wire_sync( this ).apply( data.changes, true )
			this.control_add( 'redo', event, data.title )
			return true
		}

		private control_add( source: 'undo' | 'redo', origin: $shm_hitalama_map_history_event, title: string ) {
			return this.history.event_add( $mol_wire_sync( this ).control_prepare( source, origin, title ) )
		}

		control_prepare( source: 'undo' | 'redo', origin: $shm_hitalama_map_history_event, title: string ): $shm_hitalama_map_history_data {
			return {
				id: $shm_hitalama_map_history_runtime.next_id( this.history.map() ),
				title,
				source,
				origin: origin.link().str,
				changes: [],
			}
		}

		apply( changes: readonly $shm_hitalama_map_history_change[], forward: boolean ) {
			const ordered = $mol_wire_sync( this ).changes_ordered( changes, forward )
			// A later write (or the control event) may suspend. Validation must not
			// mistake the already applied part of this operation for a foreign edit.
			$mol_wire_sync( this ).validate( ordered, forward )
			const removed = $mol_wire_sync( this ).removal_links( ordered, forward )
			if( removed.length ) $mol_wire_sync( this ).widgets_remove( removed )
			const restored = $mol_wire_sync( this ).restoration_prepare( ordered, forward )
			if( restored ) $mol_wire_sync( this ).widgets_restore( restored )
			for( const change of ordered ) {
				if( change.type === 'widget_remove' || change.type === 'widget_add' ) continue
				$mol_wire_sync( this ).change_apply( change, forward )
			}
		}

		removal_links( changes: readonly $shm_hitalama_map_history_change[], forward: boolean ) {
			return changes.filter( change => forward ? change.type === 'widget_remove' : change.type === 'widget_add' )
				.map( change => new $giper_baza_link( change.target ) )
		}

		widgets_remove( links: readonly $giper_baza_link[] ) {
			const plan=$mol_wire_sync(this).removal_prepare(links)
			// History already enumerates every removed object and every changed link.
			// Ordinary group deletion cascades; using it here also deletes children
			// that are being promoted by an ungroup event later in this same apply.
			return $shm_hitalama_map_history_runtime.silence(()=>{
				for(const ref of plan.named) ref.remote(null)
				for(const file of plan.file_links) plan.files?.cut(file)
				if(plan.add.length) plan.removed.splice(plan.add,0,0)
				plan.live.splice(plan.keep,0,plan.count)
			})
		}

		removal_prepare( links: readonly $giper_baza_link[] ) {
			const map=this.history.map(),live=map.Widgets_list(null)!,removed=map.Widgets_removed_list(null)!
			const current=live.items(),old_removed=new Set(removed.items().map(link=>link.str))
			const deleting=new Set(links.map(link=>link.str)),names=map.Widget_by_name(null),files=map.Files(null)
			files?.items()
			const widgets=current.filter(link=>deleting.has(link.str)).map(link=>map.widget_by_link(link))
			const named=widgets.flatMap(widget=>{
				const ref=names?.key(widget.title())
				return ref?.remote()?.link_str()===widget.link_str() ? [ref] : []
			})
			return {live,removed,files,named,file_links:widgets.flatMap(widget=>widget.file_links()),
				add:current.filter(link=>deleting.has(link.str)&&!old_removed.has(link.str)),
				keep:current.filter(link=>!deleting.has(link.str)),count:current.length}
		}

		restoration_prepare( changes: readonly $shm_hitalama_map_history_change[], forward: boolean ) {
			const restored = changes.filter( change => forward ? change.type === 'widget_add' : change.type === 'widget_remove' )
			if( !restored.length ) return null
			const map = this.history.map()
			const live = map.Widgets_list( null )!
			const removed = map.Widgets_removed_list( null )!
			const files = map.Files( null )!
			const live_items = live.items()
			const removed_items = removed.items()
			const file_items = files.items()
			const live_ids = new Set( live_items.map( link => link.str ) )
			const ids = new Set( restored.map( change => change.target ) )
			const add: $giper_baza_link[] = []
			for( const change of restored ) {
				if( live_ids.has( change.target ) ) continue
				live_ids.add( change.target )
				add.push( new $giper_baza_link( change.target ) )
			}
			const file_ids = new Set( file_items.map( link => link.str ) )
			const file_add: $giper_baza_link[] = []
			for( const change of restored ) {
				const refs = Array.isArray( change.meta?.files ) ? change.meta.files : ( change.meta?.file ? [ change.meta.file ] : [] )
				for( const ref of refs ) {
					if( typeof ref !== 'string' || !ref || file_ids.has( ref ) ) continue
					file_ids.add( ref )
					file_add.push( new $giper_baza_link( ref ) )
				}
			}
			return { live, removed, files, add, keep: removed_items.filter( link => !ids.has( link.str ) ), removed_count: removed_items.length, file_add }
		}

		widgets_restore( plan: NonNullable< ReturnType< $shm_hitalama_map_history_undo['restoration_prepare'] > > ) {
			// All potentially suspended reads precede writes; publish membership once.
			if( plan.file_add.length ) plan.files.splice( plan.file_add, 0, 0 )
			plan.removed.splice( plan.keep, 0, plan.removed_count )
			if( plan.add.length ) plan.live.splice( plan.add, 0, 0 )
		}

		changes_ordered( changes: readonly $shm_hitalama_map_history_change[], forward: boolean ) {
			return forward ? changes : [ ...changes ].reverse()
		}

		validate( changes: readonly $shm_hitalama_map_history_change[], forward: boolean ) {
			const removed = changes.some( change => change.type === 'widget_add' || change.type === 'widget_remove' )
				? new Set( this.history.map().Widgets_removed_list()?.items().map( link => link.str ) )
				: null
			for( const change of changes ) {
				const expected = this.value_resolve( forward ? change.before : change.after )
				if(change.type==='widgets_order') {
					const current=$mol_wire_sync(this).change_current(change) as string[]
					if(!$shm_hitalama_map_history_order.matches(current,expected)) throw new Error('Изменение нельзя отменить: объект уже изменён')
					continue
				}
				const current = change.type === 'widget_add' || change.type === 'widget_remove'
					? removed!.has( change.target )
					: $mol_wire_sync( this ).change_current( change )
				if( current === undefined ) continue
				if( !$shm_hitalama_map_history_runtime.equal( current, expected ) ) {
					throw new Error( 'Изменение нельзя отменить: объект уже изменён' )
				}
			}
		}

		change_current( change: $shm_hitalama_map_history_change ): any {
			const map = this.history.map()
			switch( change.type ) {
				case 'widgets_order': return map.Widgets_list()?.items().map(link=>link.str) ?? []
				case 'widget_set': {
					const widget = map.widget_by_link( new $giper_baza_link( change.target ) )
					return this.widget_values( widget, Object.keys( change.after ?? change.before ?? {} ) )
				}
				case 'widget_add':
				case 'widget_remove':
					// Membership is in the map's compact index. Opening every removed
					// widget land just to follow its Map backlink makes bulk undo cold.
					return Boolean( map.Widgets_removed_list()?.has( new $giper_baza_link( change.target ) ) )
				case 'cell_set': {
					const record = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.target ), $shm_hitalama_map_widget_entity_table2_record )
					const field = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.meta!.field ), $shm_hitalama_map_widget_entity_table2_field )
					return record.value( field )
				}
				case 'row_add':
				case 'row_remove': {
					const record = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.target ), $shm_hitalama_map_widget_entity_table2_record )
					return record.deleted()
				}
				case 'rows_add':
				case 'rows_remove': return ( change.meta?.rows ?? [] ).map( ( row: { record: string } ) => (
					this.history.$.$giper_baza_glob
						.Pawn( new $giper_baza_link( row.record ), $shm_hitalama_map_widget_entity_table2_record )
						.deleted()
				) )
				case 'rows_update': return ( change.meta?.cells ?? [] ).map( ( cell: { record: string, field: string } ) => {
					const record = this.history.$.$giper_baza_glob.Pawn(
						new $giper_baza_link( cell.record ),
						$shm_hitalama_map_widget_entity_table2_record,
					)
					const field = this.history.$.$giper_baza_glob.Pawn(
						new $giper_baza_link( cell.field ),
						$shm_hitalama_map_widget_entity_table2_field,
					)
					return record.value( field )
				} )
				case 'dataset_schema': {
					const dataset = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.target ), $shm_hitalama_map_widget_entity_table2_dataset )
					return this.dataset_schema_value( dataset, change )
				}
				case 'field_set': {
					const field = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.target ), $shm_hitalama_map_widget_entity_table2_field )
					return this.field_value( field, change.meta!.property )
				}
			}
			return undefined
		}

		change_apply( change: $shm_hitalama_map_history_change, forward: boolean ) {
			// This wire task can resume independently of apply(). Re-enter the
			// silent scope here so writers first reached after suspension inherit it.
			return $shm_hitalama_map_history_runtime.silence( ()=> this.change_apply_silent( change, forward ) )
		}

		private change_apply_silent( change: $shm_hitalama_map_history_change, forward: boolean ) {
			const map = this.history.map()
			const value = this.value_resolve( forward ? change.after : change.before )
			switch( change.type ) {
				case 'widgets_order': {
					const list=map.Widgets_list(null)
					const current=list?.items() ?? []
					const by_id=new Map(current.map(link=>[link.str,link]))
					const ordered=$shm_hitalama_map_history_order.merge(current.map(link=>link.str),value)
					// Reordering identities must not open widget payloads or read after writing.
					list?.splice(ordered.map(id=>by_id.get(id)!),0,current.length)
					return
				}
				case 'widget_set': {
					const widget = map.widget_by_link( new $giper_baza_link( change.target ) )
					this.widget_values_apply( widget, value )
					return
				}
				case 'widget_add':
					if( forward ) {
						map.widget_restore( new $giper_baza_link( change.target ) )
						this.widget_file_restore( change )
					} else map.widget_cut( new $giper_baza_link( change.target ) )
					return
				case 'widget_remove':
					if( forward ) map.widget_cut( new $giper_baza_link( change.target ) )
					else {
						map.widget_restore( new $giper_baza_link( change.target ) )
						this.widget_file_restore( change )
					}
					return
				case 'cell_set': {
					const record = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.target ), $shm_hitalama_map_widget_entity_table2_record )
					const field = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.meta!.field ), $shm_hitalama_map_widget_entity_table2_field )
					record.value_write( field, value )
					return
				}
				case 'row_add': {
					const record = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.target ), $shm_hitalama_map_widget_entity_table2_record )
					const removed = !forward
					if( removed ) {
						const dataset = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.meta!.dataset ), $shm_hitalama_map_widget_entity_table2_dataset )
						dataset.record_delete_links( record )
					} else {
						record.deleted( false )
						this.record_values_apply( record, change.meta?.values ?? [] )
						const dataset = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.meta!.dataset ), $shm_hitalama_map_widget_entity_table2_dataset )
						dataset.record_restore_links([ record ])
					}
					return
				}
				case 'row_remove': {
					const record = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.target ), $shm_hitalama_map_widget_entity_table2_record )
					if( forward ) {
						const dataset = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.meta!.dataset ), $shm_hitalama_map_widget_entity_table2_dataset )
						dataset.record_delete_links( record )
					} else {
						record.deleted( false )
						this.record_values_apply_raw( record, change.meta?.values ?? [] )
						this.references_apply( change, false )
						const dataset = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.meta!.dataset ), $shm_hitalama_map_widget_entity_table2_dataset )
						dataset.record_restore_links([ record ])
					}
					return
				}
				case 'rows_add': {
					const dataset = this.history.$.$giper_baza_glob.Pawn(
						new $giper_baza_link( change.meta!.dataset ),
						$shm_hitalama_map_widget_entity_table2_dataset,
					)
					const rows = change.meta?.rows ?? []
					const records = rows.map( ( row: { record: string } ) => (
						this.history.$.$giper_baza_glob.Pawn(
							new $giper_baza_link( row.record ),
							$shm_hitalama_map_widget_entity_table2_record,
						)
					) )
					const remove = !forward
					if( remove ) dataset.records_delete_links( records )
					else for( let index = 0; index < records.length; ++ index ) {
						const record = records[ index ]
						const row = rows[ index ]
						record.deleted( false )
						const fields = change.meta?.fields as readonly string[] | undefined
						this.record_values_apply( record, fields
							? fields.map( ( field, value_index )=> ({ field, value: row.values?.[ value_index ] }) )
							: row.values ?? [] )
					}
					if( !remove ) dataset.record_restore_links( records )
					return
				}
				case 'rows_remove': {
					const dataset = this.history.$.$giper_baza_glob.Pawn(
						new $giper_baza_link( change.meta!.dataset ),
						$shm_hitalama_map_widget_entity_table2_dataset,
					)
					const rows = change.meta?.rows ?? []
					const records = rows.map( ( row: { record: string } ) => (
						this.history.$.$giper_baza_glob.Pawn(
							new $giper_baza_link( row.record ),
							$shm_hitalama_map_widget_entity_table2_record,
						)
					) )
					if( forward ) dataset.records_delete_links( records )
					else {
						for( let index = 0; index < records.length; ++ index ) {
							records[ index ].deleted( false )
							this.record_values_apply_raw( records[ index ], rows[ index ].values ?? [] )
						}
						this.references_apply( change, false )
						dataset.record_restore_links( records )
					}
					return
				}
				case 'rows_update': {
					const cells = change.meta?.cells ?? []
					const values = Array.isArray( value ) ? value : []
					for( let index = 0; index < cells.length; ++ index ) {
						const cell = cells[ index ]
						const record = this.history.$.$giper_baza_glob.Pawn(
							new $giper_baza_link( cell.record ),
							$shm_hitalama_map_widget_entity_table2_record,
						)
						const field = this.history.$.$giper_baza_glob.Pawn(
							new $giper_baza_link( cell.field ),
							$shm_hitalama_map_widget_entity_table2_field,
						)
						record.value_write_raw( field, values[ index ] )
					}
					return
				}
				case 'dataset_schema': {
					const dataset = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.target ), $shm_hitalama_map_widget_entity_table2_dataset )
					if( value && typeof value === 'object' && Array.isArray( value.fields ) && Array.isArray( value.values ) ) {
						dataset.schema_restore( value )
					} else if( value && typeof value === 'object' && Array.isArray( value.fields ) ) {
						dataset.Fields( null )!.remote_list( value.fields.map( ( link: string ) =>
							this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( link ), $shm_hitalama_map_widget_entity_table2_field ),
						) )
					} else dataset.text( String( value ?? '' ) )
					return
				}
				case 'field_set': {
					const field = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( change.target ), $shm_hitalama_map_widget_entity_table2_field )
					this.field_value_apply( field, change.meta!.property, value )
					return
				}
			}
		}

		private value_resolve( value: any ): any {
			if( $shm_hitalama_map_history_text_file_is( value ) ) {
				const file = this.history.$.$giper_baza_glob.Pawn(
					new $giper_baza_link( value.hitalama_text_file ),
					$shm_hitalama_file,
				)
				return $shm_hitalama_file_text_read( file )
			}
			if( Array.isArray( value ) ) return value.map( item => this.value_resolve( item ) )
			if( !value || value.constructor !== Object ) return value
			return Object.fromEntries( Object.entries( value ).map( ([ key, item ])=> [ key, this.value_resolve( item ) ] ) )
		}

		private record_values_apply( record: $shm_hitalama_map_widget_entity_table2_record, values: readonly { field: string, value: any }[] ) {
			for( const item of values ) {
				const field = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( item.field ), $shm_hitalama_map_widget_entity_table2_field )
				record.value_write( field, item.value )
			}
		}

		private record_values_apply_raw( record: $shm_hitalama_map_widget_entity_table2_record, values: readonly { field: string, value: any }[] ) {
			for( const item of values ) {
				const field = this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( item.field ), $shm_hitalama_map_widget_entity_table2_field )
				record.value_write_raw( field, this.value_resolve( item.value ) )
			}
		}

		private references_apply( change: $shm_hitalama_map_history_change, forward: boolean ) {
			for( const item of change.meta?.references ?? [] ) {
				const record = this.history.$.$giper_baza_glob.Pawn(
					new $giper_baza_link( item.record ),
					$shm_hitalama_map_widget_entity_table2_record,
				)
				const field = this.history.$.$giper_baza_glob.Pawn(
					new $giper_baza_link( item.field ),
					$shm_hitalama_map_widget_entity_table2_field,
				)
				record.value_write_raw( field, this.value_resolve( forward ? item.after : item.before ) )
			}
		}

		private dataset_schema_value(
			dataset: $shm_hitalama_map_widget_entity_table2_dataset,
			change: $shm_hitalama_map_history_change,
		) {
			const structured = [ change.before, change.after ].some( value =>
				value && typeof value === 'object' && Array.isArray( value.fields ),
			)
			if( !structured ) return dataset.text()
			const order_only = [ change.before, change.after ].every( value =>
				value && typeof value === 'object' && Array.isArray( value.fields )
					&& Object.keys( value ).length === 1,
			)
			if( order_only ) return { fields: dataset.fields().map( field => field.link().str ) }
			return dataset.schema_snapshot()
		}

		private widget_file_restore( change: $shm_hitalama_map_history_change ) {
			const legacy = change.meta?.file
			const source = change.meta?.files
			const refs = Array.isArray( source )
				? source.filter( ( file ): file is string => typeof file === 'string' && Boolean( file ) )
				: typeof legacy === 'string' && legacy ? [ legacy ] : []
			const files = this.history.map().Files( null )!
			for( const ref of refs ) {
				const link = new $giper_baza_link( ref )
				if( !files.has( link ) ) files.add( link )
			}
		}

		private field_value( field: $shm_hitalama_map_widget_entity_table2_field, property: string ) {
			const value = ( field as any )[ property ]()
			if( value instanceof $giper_baza_pawn ) return value.link().str
			return value
		}

		private field_value_apply( field: $shm_hitalama_map_widget_entity_table2_field, property: string, value: any ) {
			if( property === 'target_dataset' ) {
				field.target_dataset( value ? this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( value ), $shm_hitalama_map_widget_entity_table2_dataset ) : null )
				return
			}
			if( property === 'display_field' || property === 'inverse_field' ) {
				;( field as any )[ property ]( value ? this.history.$.$giper_baza_glob.Pawn( new $giper_baza_link( value ), $shm_hitalama_map_widget_entity_table2_field ) : null )
				return
			}
			;( field as any )[ property ]( value )
		}

		private widget_values( widget: $shm_hitalama_map_widget, keys: readonly string[] ) {
			const render = widget.render_data()
			const result: Record< string, any > = {}
			for( const key of keys ) {
				if( key === 'text' ) result[ key ] = widget.text()
				else if( key in render ) result[ key ] = ( render as any )[ key ]
				else if( key === 'widgets' ) result[ key ] = widget.widgets().map( item => item.link().str )
				else if( [ 'container_widget', 'settings_widget', 'controlled_widget', 'view_widget', 'code_widget' ].includes( key ) ) result[ key ] = ( widget as any )[ key ]()?.link().str ?? ''
				else result[ key ] = ( widget as any )[ key ]()
			}
			return result
		}

		private widget_values_apply( widget: $shm_hitalama_map_widget, values: Record< string, any > ) {
			const render: Record< string, any > = {}
			const reactive_channels = new Set([
				'title', 'text', 'body_x', 'body_y', 'right_edge_x', 'bottom_edge_y', 'top_edge_y', 'left_edge_x', 'opacity',
			])
			for( const key in values ) {
				if( reactive_channels.has( key ) ) ( widget as any )[ key ]( values[ key ] )
				else if( key in widget.render_data() ) render[ key ] = values[ key ]
				else if( key === 'widgets' ) widget.widgets(
					values[ key ].map( ( link: string )=> widget.map().widget_by_link( new $giper_baza_link( link ) ) ),
				)
				else if( [ 'container_widget', 'settings_widget', 'controlled_widget', 'view_widget', 'code_widget' ].includes( key ) ) {
					;( widget as any )[ key ]( values[ key ] ? widget.map().widget_by_link( new $giper_baza_link( values[ key ] ) ) : null )
				} else ( widget as any )[ key ]( values[ key ] )
			}
			if( Object.keys( render ).length ) widget.render_data_patch( render )
		}

	}

}
